import { createContext, ReactNode, useContext, useState } from 'react'
import { useAsync } from 'react-use'
import { useFlag, useUnleashContext } from '@unleash/proxy-client-react'

import { useAccountContext } from 'components/contexts/AccountContext'
import { UNLEASH_FEATURE_FLAGS } from 'utils/constants'
import { useConnectedUserContext } from '../contexts/ConnectedUserContext'
import OrganizationContext from '../contexts/OrganizationContext'
export interface UnleashConfig {
  copilotAIEnabled: boolean
  context: {
    [key: string]: string
  }
  searchAccountsDropdownEnabled: boolean
  gotoContactsEnabled: boolean
  tenantsEnabled: boolean
  gotoLaunchpadEnabled: boolean
  inboxSettingsIntegrationEnabled: boolean
  resolveRebrandingEnabled?: boolean
}

export const UnleashContext = createContext<UnleashConfig>({} as UnleashConfig)

const UnleashProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { accountKey, orgId } = useAccountContext()
  const connectedUser = useConnectedUserContext()
  const updateContext = useUnleashContext()
  const pbx = useContext(OrganizationContext)
  const [context, setContext] = useState({})

  const copilotAIEnabled = useFlag(UNLEASH_FEATURE_FLAGS.copilotAI)
  const searchAccountsDropdownEnabled = useFlag(UNLEASH_FEATURE_FLAGS.searchAccountsDropdown)
  const gotoContactsEnabled = useFlag(UNLEASH_FEATURE_FLAGS.gotoAdminContacts)
  const tenantsEnabled = useFlag(UNLEASH_FEATURE_FLAGS.tenants)
  const gotoLaunchpadEnabled = useFlag(UNLEASH_FEATURE_FLAGS.showLaunchpad)
  const inboxSettingsIntegrationEnabled = useFlag(UNLEASH_FEATURE_FLAGS.inboxSettingsIntegration)
  const resolveRebrandingEnabled = useFlag(UNLEASH_FEATURE_FLAGS.resolveRebranding)

  useAsync(async () => {
    const properties = {}
    if (accountKey) {
      properties['accountKey'] = accountKey
    }
    if (pbx?.type) {
      properties['pbxType'] = pbx.type
    }
    if (orgId) {
      properties['pbxId'] = orgId
    }
    if (connectedUser) {
      properties['email'] = connectedUser.email
    }

    await updateContext({ properties })
    setContext(properties)
  }, [pbx?.type, updateContext, accountKey])

  return (
    <UnleashContext.Provider
      value={{
        copilotAIEnabled,
        context,
        searchAccountsDropdownEnabled,
        gotoContactsEnabled,
        tenantsEnabled,
        gotoLaunchpadEnabled,
        inboxSettingsIntegrationEnabled,
        resolveRebrandingEnabled,
      }}
    >
      {children}
    </UnleashContext.Provider>
  )
}

export default UnleashProvider
